import React, { useState, useEffect, useRef, useCallback } from "react";
import { Badge, Table, Typography, Tag, Space, Menu, Button, Progress, Pagination, Spin, message } from "antd";
import { useIsMobile } from "../../hooks/useIsMobile";
import OrgCard from "./OrgCard";
import { useLocation, useHistory, Link } from "react-router-dom";
import OrgLinks from "../shared/OrgLinks";
import { decodeStr } from "../../utils";
import "./OrgsV2.css";
import queryString from "query-string";
import { FilterOutlined, EnvironmentOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import OrgsMapModal from "./OrgsMapModal";
import filteredOrgCoordsQuery from "./queries/filteredOrgCoordsQuery";
import currentBillingDefuntoStats from "./queries/currentBillingDefuntoStats";
import {
  churnRiskOrgIdsQuery,
  fetchModulesListQuery,
  orgChurnIndicatorsByOrgIdsQuery,
} from "./queries";
import OrgChurnStatus from "../shared/OrgChurnStatus";
import ChurnLegend from "../shared/ChurnLegend";
import { CHURN_AT_RISK_STATUSES } from "../shared/orgChurn";
import { selectCanonicalActiveBillingPeriodsByOrg } from "../../utils/billingPeriods";

// Import the filters
import {
  useActiveClientsFilter,
  useDynamicFilter,
} from "./filters";
import FiltersComponent, { FilterTags } from "./FiltersComponent";

const { Title, Text } = Typography;

// Custom filter dropdown component for Istanza column
const IstanzaFilterDropdown = ({
  setSelectedKeys,
  confirm,
  clearFilters,
  filters,
  handleFilterChange,
  activeFilters,
}) => {
  const handleMenuClick = (value) => {
    // Add a filter to the top filters
    handleFilterChange([
      ...activeFilters,
      {
        id: Date.now(),
        column: "tipo_istanza",
        columnName: "Istanza",
        value: value,
        displayValue: value,
        operator: "equalTo",
        logicalOperator: "AND",
      },
    ]);

    // Close the dropdown
    confirm();
  };

  return (
    <div style={{ padding: 8 }}>
      <Menu>
        {filters.map((filter) => (
          <Menu.Item
            key={filter.value}
            onClick={() => handleMenuClick(filter.value)}
          >
            {filter.text}
          </Menu.Item>
        ))}
      </Menu>
    </div>
  );
};

// Custom filter dropdown component for Stato column
const StatoFilterDropdown = ({
  setSelectedKeys,
  confirm,
  clearFilters,
  filters,
  handleFilterChange,
  activeFilters,
}) => {
  const handleMenuClick = (value) => {
    // Add a filter to the top filters
    handleFilterChange([
      ...activeFilters,
      {
        id: Date.now(),
        column: "stato",
        columnName: "Stato",
        value: value,
        displayValue: value,
        operator: "equalTo",
        logicalOperator: "AND",
      },
    ]);

    // Close the dropdown
    confirm();
  };

  return (
    <div style={{ padding: 8 }}>
      <Menu>
        {filters.map((filter) => (
          <Menu.Item
            key={filter.value}
            onClick={() => handleMenuClick(filter.value)}
          >
            {filter.text}
          </Menu.Item>
        ))}
      </Menu>
    </div>
  );
};

const OrgsV2 = (props) => {
  const location = useLocation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const queryParams = queryString.parse(location.search);
  const activeFilter = queryParams.filter;

  // State management
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [totalOrgsCount, setTotalOrgsCount] = useState(0);
  const [filteredOrgsCount, setFilteredOrgsCount] = useState(0);

  // Add flag to prevent duplicate calls
  const isInitialMount = useRef(true);
  // Add flag to track if initial data is loaded
  const initialDataLoaded = useRef(false);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const [isMapModalVisible, setIsMapModalVisible] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [mapCoordinates, setMapCoordinates] = useState([]);
  const [billingUsageMap, setBillingUsageMap] = useState({}); // { orgId: { used, start, end } }
  const [churnIndicatorMap, setChurnIndicatorMap] = useState({});
  const [churnIndicatorsLoading, setChurnIndicatorsLoading] = useState(false);
  const [churnCounts, setChurnCounts] = useState({});
  const [churnFilterLoading, setChurnFilterLoading] = useState(false);
  const [availableModules, setAvailableModules] = useState([]);
  // Removed module discounts usage; use limite_servizi as stored in DB

  // Use the dynamic filter hook instead of combined filter
  const {
    searchValue,
    setSearchValue,
    activeFilters,
    fetchFilteredData,
    handleSearchChange,
    handleFilterChange,
    clearAll,
    buildWhereClause,
  } = useDynamicFilter(
    setLoading,
    setOrgs,
    setPagination,
    setFilteredOrgsCount
  );

  // Fetch available modules for module filter dropdown
  useEffect(() => {
    let cancelled = false;
    async function loadModules() {
      try {
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: { query: fetchModulesListQuery },
          headers: { Authorization: `Bearer ${token}` },
        });
        const edges = response.data?.modules?.edges || [];
        const mods = edges
          .map(e => e.node)
          .filter(Boolean)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        if (!cancelled) setAvailableModules(mods);
      } catch (err) {
        console.error("Failed to fetch modules list:", err);
      }
    }
    loadModules();
    return () => { cancelled = true; };
  }, [getTokenSilently, env]);

  // For backwards compatibility - keep this for legacy URL handling
  const applyActiveClientsFilter = useActiveClientsFilter(
    setPagination,
    setLoading,
    setOrgs,
    setFilteredOrgsCount
  );

  // Simplified reset filters function that uses the clearAll
  const resetFilters = (executeQuery = true) => {
    // Reset pagination to page 1 when filters are reset
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));

    if (executeQuery) {
      clearAll();
    } else {
      // Just clear the filters in state without triggering a query
      setSearchValue("");
      // Pass skipQuery=true to prevent triggering a query
      handleFilterChange([], true);
    }
  };

  const handleOpenMapModal = useCallback(() => {
    setIsMapModalVisible(true);
  }, []);

  const handleCloseMapModal = useCallback(() => {
    setIsMapModalVisible(false);
    setMapCoordinates([]);
    setMapError(null);
    setMapLoading(false);
  }, []);

  useEffect(() => {
    if (!isMapModalVisible) {
      return;
    }

    let isCancelled = false;

    const loadCoordinates = async () => {
      setMapLoading(true);
      setMapError(null);

      try {
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const whereClause = buildWhereClause();
        const hasFilters = whereClause && Object.keys(whereClause).length > 0;
        const variables = hasFilters
          ? { where: { org_pointer: { have: whereClause } } }
          : {};

        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: filteredOrgCoordsQuery,
            variables,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (isCancelled) {
          return;
        }

        const coords =
          response.data?.org_coords?.edges?.map((edge) => edge.node) || [];

        const sanitizedCoords = coords
          .filter(
            (coord) =>
              coord && coord.latitude !== undefined && coord.longitude !== undefined
          )
          .map((coord) => ({
            ...coord,
            latitude:
              typeof coord.latitude === "number"
                ? coord.latitude
                : parseFloat(coord.latitude),
            longitude:
              typeof coord.longitude === "number"
                ? coord.longitude
                : parseFloat(coord.longitude),
          }))
          .filter(
            (coord) =>
              Number.isFinite(coord.latitude) && Number.isFinite(coord.longitude)
          );

        setMapCoordinates(sanitizedCoords);
      } catch (error) {
        console.error("Error fetching filtered org coordinates:", error);
        if (!isCancelled) {
          setMapError(
            "Errore nel caricamento delle coordinate. Riprova tra qualche istante."
          );
          setMapCoordinates([]);
        }
      } finally {
        if (!isCancelled) {
          setMapLoading(false);
        }
      }
    };

    loadCoordinates();

    return () => {
      isCancelled = true;
    };
  }, [isMapModalVisible, buildWhereClause, getTokenSilently, env]);

  // Update total count when there are no filters/search
  useEffect(() => {
    // When there are no filters and no search term, use the filteredOrgsCount as totalOrgsCount
    if (!searchValue && activeFilters.length === 0 && filteredOrgsCount > 0) {
      setTotalOrgsCount(filteredOrgsCount);
    }
  }, [searchValue, activeFilters, filteredOrgsCount]);

  // New effect to ensure data is loaded on initial render
  useEffect(() => {
    // This effect only runs once on mount to ensure data is loaded
    // even if location or other dependencies don't change
    const shouldFetchInitialData = isInitialMount.current;
    // Don't immediately set isInitialMount to false here, let the location effect do that

    if (shouldFetchInitialData && !initialDataLoaded.current) {
      // Parse URL params for initial fetch
      const params = queryString.parse(location.search);
      const searchTerm = params.search;
      const page = params.page ? parseInt(params.page, 10) : 1;
      const pageSize = params.pageSize ? parseInt(params.pageSize, 10) : 10;
      let parsedFilters = [];
      
      if (params.filters) {
        try {
          parsedFilters = JSON.parse(params.filters);
        } catch (error) {
          console.error("Error parsing filters from URL:", error);
        }
      }
      
      initialDataLoaded.current = true;
      
      // Set search value if present in URL
      if (searchTerm) {
        setSearchValue(searchTerm);
      }
      
      // Set pagination from URL
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: pageSize,
      }));
      
      // Set filters state if present
      if (parsedFilters.length > 0) {
        handleFilterChange(parsedFilters, true); // Skip query - we'll fetch below
      }
      
      // Fetch data with URL params including pagination
      if (searchTerm || parsedFilters.length > 0) {
        fetchFilteredData({ 
          searchTerm: searchTerm || undefined,
          filters: parsedFilters.length > 0 ? parsedFilters : undefined,
          currentPage: page,
          pageSize: pageSize,
        });
      } else {
        fetchFilteredData({ 
          force: true,
          currentPage: page,
          pageSize: pageSize,
        });
      }
    }
  }, [fetchFilteredData, location.search, setSearchValue, setPagination, handleFilterChange]);

  // Fetch current (active) billing defunto stats for the visible page (single batched query)
  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        if (!orgs || orgs.length === 0) { setBillingUsageMap({}); return; }
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const nowIso = new Date().toISOString();
        const ids = orgs.map(o => o.objectId).filter(Boolean);
        const where = {
          org_pointer: { have: { objectId: { in: ids } } },
          period_type: { equalTo: 'billing' },
          // Use the same date_ref used in the Org page billing view
          date_ref: { equalTo: 'created_at' },
          period_start: { lessThanOrEqualTo: nowIso },
          period_end: { greaterThan: nowIso },
        };
        const variables = { first: ids.length * 5, where, order: [ 'period_start_ASC' ] };
        console.log('[BillingStats] Fetching current billing defunto stats', { count: ids.length, ids, variables });
        const resp = await axios({ method: 'post', url: graphqlUrl, data: { query: currentBillingDefuntoStats, variables }, headers: { Authorization: `Bearer ${token}` } });
        if (!resp?.data) {
          console.warn('[BillingStats] Empty response');
        } else {
          console.log('[BillingStats] Raw response keys', Object.keys(resp.data));
        }
        const edges = resp?.data?.defunto_period_stats?.edges || [];
        console.log('[BillingStats] Edges length', edges.length, edges.slice(0, 3));
        const map = selectCanonicalActiveBillingPeriodsByOrg(
          edges.map((edge) => edge.node),
          Date.now()
        );
        console.log('[BillingStats] Map built', map);
        if (!cancelled) setBillingUsageMap(map);
      } catch (e) {
        // Do not break table rendering on errors; just leave map empty
        console.error('Failed fetching billing stats', e);
        if (!cancelled) setBillingUsageMap({});
      }
    }
    run();
    return () => { cancelled = true; };
  }, [orgs, getTokenSilently, env]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!orgs || orgs.length === 0) {
        setChurnIndicatorMap({});
        setChurnIndicatorsLoading(false);
        return;
      }

      setChurnIndicatorsLoading(true);
      try {
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const ids = orgs.map((org) => org.objectId).filter(Boolean);
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: orgChurnIndicatorsByOrgIdsQuery,
            variables: {
              first: Math.max(ids.length * 2, ids.length),
              where: {
                org_pointer: { have: { objectId: { in: ids } } },
              },
              order: ["updatedAt_DESC"],
            },
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        const map = {};
        const edges = response.data?.org_churn_indicators?.edges || [];
        edges.forEach((edge) => {
          const node = edge.node;
          const orgId = node?.org_pointer?.objectId;
          if (!orgId || map[orgId]) return;
          map[orgId] = node;
        });

        if (!cancelled) setChurnIndicatorMap(map);
      } catch (error) {
        console.error("Failed fetching org churn indicators", error);
        if (!cancelled) setChurnIndicatorMap({});
      } finally {
        if (!cancelled) setChurnIndicatorsLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [orgs, getTokenSilently, env]);

  // Conteggi per i badge nel menu filtri: una fetch leggera (first: 1) per
  // ciascuno stato "a rischio"; l'aggregato "Org a rischio" è la loro somma.
  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const counts = await Promise.all(
          CHURN_AT_RISK_STATUSES.map((status) =>
            axios({
              method: "post",
              url: graphqlUrl,
              data: {
                query: churnRiskOrgIdsQuery,
                variables: {
                  first: 1,
                  where: { status: { equalTo: status } },
                },
              },
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r) => r.data?.org_churn_indicators?.count ?? 0)
              .catch(() => 0)
          )
        );

        if (!cancelled) {
          const map = {};
          CHURN_AT_RISK_STATUSES.forEach((status, i) => {
            map[status] = counts[i];
          });
          setChurnCounts(map);
        }
      } catch (error) {
        console.error("Failed fetching churn counts", error);
        if (!cancelled) setChurnCounts({});
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [getTokenSilently, env]);

  const applyChurnFilter = useCallback(
    async (statuses, label) => {
      setChurnFilterLoading(true);

      try {
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: churnRiskOrgIdsQuery,
            variables: { first: 1000, where: { status: { in: statuses } } },
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        const ids = [
          ...new Set(
            (response.data?.org_churn_indicators?.edges || [])
              .map((edge) => edge.node?.org_pointer?.objectId)
              .filter(Boolean)
          ),
        ];

        handleFilterChange([
          {
            id: Date.now(),
            column: "objectId",
            columnName: "Churn",
            value: ids,
            displayValue: `${ids.length} ${label}`,
            operator: "in",
            logicalOperator: "AND",
          },
        ]);

        if (ids.length === 0) {
          message.info(`Nessuna org ${label}`);
        } else {
          message.success(`Filtrate ${ids.length} org ${label}`);
        }
      } catch (error) {
        console.error("Failed applying churn filter", error);
        message.error("Errore nel caricamento del filtro churn");
      } finally {
        setChurnFilterLoading(false);
      }
    },
    [env, getTokenSilently, handleFilterChange]
  );

  // Removed module discounts fetch

  // Handle pagination change
  const handleTableChange = (newPagination, filters, sorter) => {
    const currentPage = newPagination.current;
    const pageSize = newPagination.pageSize;

    console.log("Table change:", {
      currentPage,
      pageSize,
      total: newPagination.total,
      sorter,
    });

    // Update pagination state first
    setPagination((prev) => ({
      ...prev,
      current: currentPage,
      pageSize: pageSize,
      // Ensure total is preserved and consistent with the data source
      total: (searchValue || activeFilters.length > 0) ? filteredOrgsCount : totalOrgsCount,
    }));

    // Use the dynamic filter with pagination and sorting params
    fetchFilteredData({
      pageSize: pageSize,
      currentPage: currentPage,
      sortField: sorter.field,
      sortOrder: sorter.order,
    });

    // Check if any columns are being filtered
    const hasActiveFilters = Object.values(filters).some(
      (f) => f && f.length > 0
    );

    // If filters are active, count the rows that match the filters
    if (hasActiveFilters) {
      // This only works for client-side filtering
      // For server-side filtering, you'd need to update during the fetch response

      // We can use the table's current data - Column filters are applied automatically
      // by the Table component, so we just need to update the badge count
      setTimeout(() => {
        // Use setTimeout to wait for the table to apply filters internally
        // Get the actual number of rows displayed in the filtered table
        const filteredCount = document.querySelectorAll(
          ".ant-table-tbody tr:not(.ant-table-placeholder)"
        ).length;
        setFilteredOrgsCount(filteredCount);

        // Update pagination total to match filtered count
        setPagination((prev) => ({
          ...prev,
          total: filteredCount,
        }));
      }, 0);
    }
  };


  // Fetch data when component mounts or when location changes
  useEffect(() => {
    const params = queryString.parse(location.search);
    const searchTerm = params.search;
    const currentSearch = location.search;

    // Skip if the change came from a filter operation with skipQuery flag
    if (location.state && location.state.skipQuery) {
      return;
    }

    // For initial render, let the dedicated effect handle it
    if (isInitialMount.current) {
      isInitialMount.current = false;

      // Update states based on URL params, but don't trigger a fetch here
      if (searchTerm) {
        setSearchValue(searchTerm);
      }

      if (params.filters) {
        try {
          const parsedFilters = JSON.parse(params.filters);
          if (Array.isArray(parsedFilters) && parsedFilters.length > 0) {
            // Just update the filter state without triggering a fetch
            handleFilterChange(parsedFilters, true);
          }
        } catch (error) {
          console.error("Error parsing filters from URL:", error);
        }
      }

      return;
    }

    // Avoid reprocessing the same search
    if (currentSearch === location.search) {
      return;
    }

    if (searchTerm) {
      setSearchValue(searchTerm);
      const page = params.page ? parseInt(params.page, 10) : 1;
      const pageSize = params.pageSize ? parseInt(params.pageSize, 10) : 10;
      fetchFilteredData({ 
        searchTerm,
        currentPage: page,
        pageSize: pageSize
      });
    } else if (activeFilter === "active") {
      // For backward compatibility
      applyActiveClientsFilter(pagination.pageSize, pagination.current);
    } else if (params.filters) {
      // If there are filters in the URL but no search term, just fetch with filters
      try {
        const parsedFilters = JSON.parse(params.filters);
        if (Array.isArray(parsedFilters) && parsedFilters.length > 0) {
          const page = params.page ? parseInt(params.page, 10) : 1;
          const pageSize = params.pageSize ? parseInt(params.pageSize, 10) : 10;
          fetchFilteredData({ 
            filters: parsedFilters,
            currentPage: page,
            pageSize: pageSize
          });
        }
      } catch (error) {
        console.error("Error parsing filters from URL:", error);
        fetchFilteredData({ force: true });
      }
    } else {
      // Use the dynamic filter with no search/filters
      if (!loading) {
        fetchFilteredData({ force: true });
      }
    }
  }, [
    location.search,
    fetchFilteredData,
    activeFilter,
    applyActiveClientsFilter,
    pagination.pageSize,
    loading,
    setSearchValue,
    location.state,
    pagination,
    handleFilterChange,
  ]);

  // Update search and pagination in URL when they change
  useEffect(() => {
    const currentParams = queryString.parse(location.search);
    const newParams = {
      ...currentParams,
      search: searchValue || undefined,
      page: pagination.current > 1 ? pagination.current : undefined,
      pageSize: pagination.pageSize !== 10 ? pagination.pageSize : undefined,
    };

    // Remove undefined values
    Object.keys(newParams).forEach((key) => {
      if (newParams[key] === undefined) {
        delete newParams[key];
      }
    });

    const newSearch = queryString.stringify(newParams);
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    // Update URL without adding to browser history
    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl, { skipQuery: true });
    }
  }, [searchValue, pagination, location.pathname, location.search, history]);

  // Don't re-fetch when pageSize changes - let the table handle it
  // This was causing an infinite loop in the original code

  function onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
  }

  // Table columns definition
  const columns = [
    {
      title: "ID",
      dataIndex: "objectId",
      key: "objectId",
      render: (orgId) => {
        return <Link to={`/orgs/${orgId}`}>{orgId}</Link>;
      },
    },
    {
      title: "Nome Azienda",
      dataIndex: "nome",
      key: "nome",
      render: (nome, record) => {
        return <Link to={`/orgs/${record.objectId}`}>{decodeStr(nome)}</Link>;
      },
      sorter: true, // Enable sorting without providing compare function
    },
    {
      title: "Istanza",
      key: "tipo_istanza",
      dataIndex: "tipo_istanza",
      // Custom filter dropdown that adds a filter to the top filters
      filterDropdown: (props) => (
        <IstanzaFilterDropdown
          {...props}
          filters={
            orgs.length > 0
              ? orgs
                  .map((item) => item.tipo_istanza)
                  .filter(Boolean)
                  .filter(onlyUnique)
                  .sort()
                  .map((value) => ({
                    text: value,
                    value,
                  }))
              : [
                  // Default values if orgs is empty
                  { text: "free", value: "free" },
                  { text: "demo", value: "demo" },
                  { text: "production", value: "production" },
                ]
          }
          handleFilterChange={handleFilterChange}
          activeFilters={activeFilters}
        />
      ),
      // Keep the filter icon visible
      filterIcon: () => {
        // Check if there's an active filter for tipo_istanza
        const hasActiveFilter = activeFilters.some(
          (filter) => filter.column === "tipo_istanza"
        );
        return (
          <FilterOutlined
            style={{ color: hasActiveFilter ? "#1890ff" : undefined }}
          />
        );
      },
      // Remove the onFilter property as we're handling filtering through the top filters
      render: (tipoIstanza = "", record) => {
        if (!tipoIstanza) {
          return null;
        }

        const colors = {
          free: "blue",
          demo: "orange",
          production: "green",
        };

        return (
          <Tag color={colors[tipoIstanza]}>{tipoIstanza.toUpperCase()}</Tag>
        );
      },
    },
    {
      title: "Stato",
      key: "stato",
      dataIndex: "stato",
      // Custom filter dropdown that adds a filter to the top filters
      filterDropdown: (props) => (
        <StatoFilterDropdown
          {...props}
          filters={[
            { text: "attiva", value: "attiva" },
            { text: "disattiva", value: "disattiva" },
          ]}
          handleFilterChange={handleFilterChange}
          activeFilters={activeFilters}
        />
      ),
      // Keep the filter icon visible
      filterIcon: () => {
        // Check if there's an active filter for stato
        const hasActiveFilter = activeFilters.some(
          (filter) => filter.column === "stato"
        );
        return (
          <FilterOutlined
            style={{ color: hasActiveFilter ? "#1890ff" : undefined }}
          />
        );
      },
      render: (stato) => {
        if (!stato) {
          return null;
        }
        const color = stato === "attiva" ? "green" : "red";
        return <Tag color={color}>{stato.toUpperCase()}</Tag>;
      },
    },
    {
      title: (
        <Space size={4}>
          Churn
          <ChurnLegend />
        </Space>
      ),
      key: "churn",
      render: (_, record) => (
        <OrgChurnStatus
          indicator={churnIndicatorMap[record.objectId]}
          loading={churnIndicatorsLoading}
          hideNotApplicable
        />
      ),
      width: 180,
    },
    {
      title: "Servizi",
      key: "utilizzo_col",
      render: (_, record) => {
        const ista = (record?.tipo_istanza || '').toLowerCase();
        if (ista === 'demo' || ista === 'free') return null;
        const s = billingUsageMap?.[record.objectId];
        if (!s) return null;
        const flat = !!s.limit_is_flat;
        const toNum = (v) => (typeof v === 'number' ? v : (v != null ? parseFloat(String(v).replace(',', '.')) : null));
        const used = toNum(s.total_count);
        const limit = toNum(s.purchased_amount);
        if (flat) {
          return <span>{Number.isFinite(used) ? used : '-'} / FLAT</span>;
        }
        const pctRaw = (typeof s.pct_used === 'number') ? s.pct_used : (
          (Number.isFinite(used) && Number.isFinite(limit) && limit > 0) ? ((used / limit) * 100) : null
        );
        const pct = Number.isFinite(pctRaw) ? Math.round(pctRaw) : null;
        const pctClamped = Number.isFinite(pctRaw) ? Math.max(0, Math.min(100, Math.round(pctRaw))) : 0;
        const strokeColor = (function() {
          if (!Number.isFinite(pctRaw)) return undefined;
          if (pctRaw > 100 || (Number.isFinite(used) && Number.isFinite(limit) && used > limit)) return '#52c41a';
          if (pctRaw < 50) return '#f5222d';
          if (pctRaw < 80) return '#fa8c16';
          return '#3D5864';
        })();
        return (
          <div style={{ minWidth: 160 }}>
            <Progress
              percent={pctClamped}
              size="small"
              showInfo
              strokeColor={strokeColor}
              format={() => (pct != null ? `${pct}%` : '')}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span>{Number.isFinite(used) ? used : '-'} / {Number.isFinite(limit) ? limit : '-'}</span>
            </div>
          </div>
        );
      },
      width: 200,
    },
    {
      title: "Proiezione",
      key: "proiezione_col",
      render: (_, record) => {
        const ista = (record?.tipo_istanza || '').toLowerCase();
        if (ista === 'demo' || ista === 'free') return null;
        const s = billingUsageMap?.[record.objectId];
        if (!s) return null;
        if (s.needs_more_data) return null;
        if (!s.prediction_available) return <Tag>n/d</Tag>;
        const toNum = (v) => (typeof v === 'number' ? v : (v != null ? parseFloat(String(v).replace(',', '.')) : null));
        const pctRaw = toNum(s.pct_projected);
        const pct = Number.isFinite(pctRaw) ? Math.round(pctRaw) : null;
        const pctClamped = Number.isFinite(pctRaw) ? Math.max(0, Math.min(100, Math.round(pctRaw))) : 0;
        const strokeColor = (function() {
          if (!Number.isFinite(pctRaw)) return undefined;
          if (pctRaw > 100 || (typeof s.pct_used === 'number' && s.pct_used > 100)) return '#52c41a';
          if (pctRaw < 50) return '#f5222d';
          if (pctRaw < 80) return '#fa8c16';
          return '#3D5864';
        })();
        const exp = toNum(s.expected_total);
        return (
          <div style={{ minWidth: 160 }}>
            <Progress
              percent={pctClamped}
              size="small"
              showInfo
              strokeColor={strokeColor}
              format={() => (pct != null ? `${pct}%` : '')}
            />
            {Number.isFinite(exp) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12 }}>
                <Text type="secondary">{Math.round(exp)}</Text>
              </div>
            )}
          </div>
        );
      },
      width: 200,
    },
    {
      title: "Links",
      key: "email",
      dataIndex: "email",
      render: (_, record) => {
        return <OrgLinks org={record} />;
      },
    },
  ];

  return (
    <div style={{ margin: "16px 16px" }}>
      <div className="orgs-header">
        <Title level={3} style={{ margin: 0 }}>
          <Space>
            Organizzazioni
            <Badge
              overflowCount={5000}
              count={(searchValue || activeFilters.length > 0) ? filteredOrgsCount : totalOrgsCount}
              title={
                (searchValue || activeFilters.length > 0)
                  ? `Showing ${filteredOrgsCount} of ${totalOrgsCount} organizations`
                  : `Total: ${totalOrgsCount} organizations`
              }
            />
          </Space>
        </Title>
        <Space
          className="orgs-controls"
          wrap
          direction="vertical"
          style={{ width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Button
              icon={<EnvironmentOutlined />}
              onClick={handleOpenMapModal}
              loading={mapLoading && isMapModalVisible}
            >
              Visualizza su Mappa
            </Button>
            <FiltersComponent
              resetFilters={resetFilters}
              onFilterChange={handleFilterChange}
              activeFilters={activeFilters}
              showTagsInline={false}
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              onChurnFilter={applyChurnFilter}
              churnFilterLoading={churnFilterLoading}
              churnCounts={churnCounts}
              modules={availableModules}
              onSearch={(value, _, { source } = {}) => {
                // Reset pagination to page 1 when search is performed
                setPagination((prev) => ({
                  ...prev,
                  current: 1,
                }));

                // If clearing (clicking X button or using clear API), explicitly set empty string
                if (source === "clear") {
                  setSearchValue("");
                  fetchFilteredData({ searchTerm: "", currentPage: 1 });
                } else {
                  fetchFilteredData({ searchTerm: value, currentPage: 1 });
                }
              }}
            />
          </div>
          {/* Display filter tags on a separate line */}
          <FilterTags
            activeFilters={activeFilters}
            onRemoveFilter={(filterId) =>
              handleFilterChange(activeFilters.filter((f) => f.id !== filterId))
            }
          />
        </Space>
      </div>

      {isMobile ? (
        // Mobile: Card layout
        <Spin spinning={loading}>
          <div className="org-cards-container">
            {orgs.map((org) => (
              <OrgCard
                key={org.objectId}
                org={org}
                billingUsageMap={billingUsageMap}
                churnIndicator={churnIndicatorMap[org.objectId]}
                churnLoading={churnIndicatorsLoading}
              />
            ))}
          </div>
          <div className="org-cards-pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={(searchValue || activeFilters.length > 0) ? filteredOrgsCount : totalOrgsCount}
              size="small"
              showSizeChanger={false}
              showQuickJumper={false}
              onChange={(page, pageSize) => handleTableChange({ current: page, pageSize }, {}, {})}
            />
          </div>
        </Spin>
      ) : (
        // Desktop: Table layout
        <Table
          dataSource={orgs}
          columns={columns}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: [
              "10",
              "20",
              "30",
              "50",
              "100",
              "200",
              "500",
              "1000",
              "2000",
              "5000",
            ],
            total: (searchValue || activeFilters.length > 0) ? filteredOrgsCount : totalOrgsCount,
            current: pagination.current,
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          rowKey="objectId"
        />
      )}

      <OrgsMapModal
        visible={isMapModalVisible}
        loading={mapLoading}
        onClose={handleCloseMapModal}
        coordinates={mapCoordinates}
        error={mapError}
      />

    </div>
  );
};

export default OrgsV2;
